import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  IRequirementsMandatoryNew,
  IStructuresUnit,
} from 'APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { Button } from 'UIPackage/MainModule/components/Button';
import { fetchMandatoryRequirementsListNew } from 'APIPackage/RequirementsNpaModule/services/requirementsNpaApi';

import styles from './StructuresUnitItem.module.scss';
import { InputFormRequirementsNpa, ISelectedStructuralUnit } from '../interface';
import { Loader } from '../Loader';
import { IKnmErknmFormRequirementNpa } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IKnmPmFormRequirementsNpa } from '../../../KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { convertFormDateValueToKnmDataDate } from '../../MainModule/common/convertFormDateValueToKnmDataDate';

interface IStructuresUnitItemProps {
  _key: string;
  addMandatory: (path: string, suId: number) => void;
  created: string | null | undefined;
  didCloseInnerModal: boolean;
  ignoredNpaIds?: number[];
  inputRequirementsNpa?: InputFormRequirementsNpa[];
  isDefaultOpen?: boolean;
  isSusOpen: {
    [key: string]: boolean;
  };
  item: IStructuresUnit;
  knoVersionId: string;
  mrIds?: number[];
  npaId: number;
  paddingPx: number;
  path: string;
  requirementsErknmDisabled: boolean | undefined;
  setIsSuOpen: (path: string, val: boolean) => void;
  setSuIdsSelected: Dispatch<SetStateAction<ISelectedStructuralUnit[]>>;
  structuresUnitsFilters?: {
    objectTypeId?: string[];
    objectKindId?: string[];
    objectSubkindId?: string[];
    riskIndicatorIds?: string[];
  };
  suIdsSelected: ISelectedStructuralUnit[];
  supervisionVersionId: string;
  updateNpaList: (npaId: number, suId: string, list: IRequirementsMandatoryNew[]) => void;
  usedNpa: IKnmErknmFormRequirementNpa[] | IKnmPmFormRequirementsNpa[] | undefined;
  withRequirements: boolean;
}

const StructuresUnitItem = ({
  _key,
  addMandatory,
  created,
  didCloseInnerModal,
  ignoredNpaIds,
  inputRequirementsNpa,
  isDefaultOpen = false,
  isSusOpen,
  item,
  knoVersionId,
  mrIds,
  npaId,
  paddingPx,
  path,
  requirementsErknmDisabled,
  setIsSuOpen,
  setSuIdsSelected,
  structuresUnitsFilters,
  suIdsSelected,
  supervisionVersionId,
  updateNpaList,
  usedNpa,
  withRequirements,
}: IStructuresUnitItemProps) => {
  const isDefaultOpenByIsSusOpen = Object.keys(isSusOpen).find(
    key => key.indexOf(`${npaId}/${path}`) === 0 && isSusOpen[key],
  );

  const [isOpen, setIsOpen] = useState(isDefaultOpenByIsSusOpen || isDefaultOpen);
  const [isLoading, setIsLoading] = useState(false);

  const isSelectedSUById = useMemo(() => {
    return !!suIdsSelected.find(i => i.id === item.id);
  }, [item, suIdsSelected]);

  const isSelectedSUByPath = useMemo(() => {
    return !!suIdsSelected.find(i => {
      const isLongPath = i.path.indexOf('\\') > -1;

      return (
        i.npaId === npaId &&
        i.path.indexOf(isLongPath && item.children?.length ? `${path}\\` : path) > -1 &&
        (i.path === path || (item.id && !item.children?.length) ? item.id === i.id : true)
      );
    });
  }, [npaId, suIdsSelected, path, item]);

  const onSelectedSU = useCallback(() => {
    const suId = item.id;

    if (!suId) {
      return;
    }

    setSuIdsSelected(prev => {
      if (!prev.some(i => i.id === suId)) {
        const newSelectedItem: ISelectedStructuralUnit = {
          id: suId,
          path,
          npaId,
        };

        return [...prev, newSelectedItem];
      }

      return prev.filter(i => i.id !== suId);
    });
  }, [item.id, path, npaId, setSuIdsSelected]);

  const selectedMandatoryById = useCallback(
    (mandatoryId: number) => {
      return !!suIdsSelected.find(i => i.mandatoryId === mandatoryId);
    },
    [item, suIdsSelected],
  );

  // При первом монтировании узла дерева СЕ — начальный автовыбор без участия пользователя.
  // Зависимости пустые: эффект выполняется один раз на каждый экземпляр StructuresUnitItem.
  useEffect(() => {
    if (didCloseInnerModal) return;

    const isUsedNpa = usedNpa?.some(i => i.npa.id === npaId);

    // Листовая СЕ с одной уже загруженной формулировкой — сразу отмечаем её в suIdsSelected.
    if (
      isOpen &&
      withRequirements &&
      (!item.children || item.children.length === 0) &&
      item.data &&
      item.data.length === 1 &&
      !selectedMandatoryById(item.data[0].id) &&
      !isUsedNpa
    ) {
      onSelectedMandatory(item.data[0].id, false);
    }

    // У родительской СЕ с дочерними узлами — автовыбор формулировок у детей,
    // у которых в дереве ровно одна доступная формулировка (dataSize === 1).
    if (isOpen && withRequirements && item.children?.length !== 0 && !isUsedNpa) {
      setSuIdsSelected(prev => {
        const newList = [...prev];
        let isChanged = false;

        item.children.forEach(subUnit => {
          if (subUnit.dataSize === 1 && subUnit.data?.length === 1) {
            const mandatoryId = subUnit.data[0].id;

            if (newList.some(i => i.mandatoryId === mandatoryId)) {
              return;
            }

            newList.push({
              id: subUnit.id || '',
              path: `${path}\\${subUnit.levelTitle}: ${subUnit.value}`,
              npaId,
              mandatoryId,
            });
            isChanged = true;
          }
        });

        return isChanged ? newList : prev;
      });
    }

    // Режим без формулировок: листовая СЕ при открытом узле — отмечаем саму СЕ (без mandatoryId).
    if (!withRequirements && isOpen && item.id && !item.children?.length && !isSelectedSUById) {
      onSelectedSU();
    }
  }, []);

  // Догрузка полного списка формулировок для СЕ, если в дереве пришла только часть (data.length < dataSize).
  // После ответа API обновляем локальный список в модалке и восстанавливаем галочки у уже выбранных в паспорте ОТ.
  useEffect(() => {
    if (withRequirements && isOpen && item.id && item.dataSize && item.data?.length !== item.dataSize) {
      setIsLoading(true);
      fetchMandatoryRequirementsListNew({
        npaId,
        suId: item.id,
        knoVersionId,
        supervisionVersionId,
        knmCreationDate: convertFormDateValueToKnmDataDate(created) || undefined,
        structuresUnitsFilters,
        mrIds,
      }).then(list => {
        let resultList = list;

        // resultList уже отфильтрован на беке, если мы передали mrIds
        if (inputRequirementsNpa && !mrIds?.length) {
          const mandatoryIds = inputRequirementsNpa.flatMap(req => req?.mandatory.map(mandatory => mandatory?.id));

          if (mandatoryIds && mandatoryIds.length > 0) {
            resultList = list.filter(listItem => mandatoryIds.includes(listItem.id));
          }
        }

        // Кладём загруженные формулировки в локальное состояние модалки (отрисовка чекбоксов).
        updateNpaList(npaId, item.id || '', resultList);

        const isUsedNpa = usedNpa?.some(i => i.npa.id === npaId);

        // Единственная доступная формулировка — автовыбор (toggle = false: только добавить, не снимать).
        if (resultList.length === 1 && !isUsedNpa) {
          onSelectedMandatory(resultList[0].id, false);
        } else {
          // Несколько формулировок: отмечаем те, что уже есть в паспорте КНМ (ignoredNpaIds).
          resultList
            .filter(e => ignoredNpaIds?.includes(e.id))
            ?.forEach(mandatory => {
              onSelectedMandatory(mandatory.id, false);
            });
        }
        setIsLoading(false);
      });
    }
  }, [isOpen, item.data?.length, item.dataSize, ignoredNpaIds]);

  const setIsOpenFunction = useCallback(() => {
    setIsOpen(!isOpen);
    setIsSuOpen(`${npaId}/${path}`, !isOpen);

    if (
      !isOpen &&
      withRequirements &&
      !inputRequirementsNpa &&
      item.data &&
      item.data.length === 1 &&
      !selectedMandatoryById(item.data[0].id)
    ) {
      onSelectedMandatory(item.data[0].id, false);
    }
    if (!isOpen && !withRequirements && item.id && !item.children?.length && !isSelectedSUById) {
      onSelectedSU();
    }
  }, [isOpen, npaId, path, withRequirements, item, selectedMandatoryById, isSelectedSUById]);

  const onSelectedMandatory = useCallback(
    (mandatoryId: number, toggle: boolean = true) => {
      const suId = item.id || '';

      setSuIdsSelected(prev => {
        const isMandatoryExists = prev.some(i => i.mandatoryId === mandatoryId);

        if (!isMandatoryExists) {
          return [...prev, { id: suId, path, npaId, mandatoryId }];
        }

        if (!toggle) {
          return prev;
        }

        return prev.filter(i => i.mandatoryId !== mandatoryId);
      });
    },
    [item.id, path, npaId, setSuIdsSelected],
  );

  const onClickSU = useCallback(() => {
    if (!withRequirements && !item.children?.length) {
      onSelectedSU();
    }
  }, [withRequirements, onSelectedSU, item]);

  const mandatoryTitle = useMemo(() => {
    if (!item.sourceRot) {
      return 'ЕРКНМ';
    }

    if (!item.data || !item.data.length) return 'ФГИС РОТ';

    const hasOneFromRot = item.data.find(i => i.sourceRot);
    const hasOneFromErknm = item.data.find(i => !i.sourceRot);

    if (hasOneFromRot && hasOneFromErknm) {
      return 'ФГИС РОТ и ЕРКНМ';
    }

    return item.data[0].sourceRot ? 'ФГИС РОТ' : 'ЕРКНМ';
  }, [item]);

  const renderChildren = (children: IStructuresUnit[]) => {
    return children?.map((subUnit, i) => {
      // Скрываем пустые элементы, на их месте (в иерархии дерева) сразу рендерим их дочерние элементы
      if (subUnit.value === '-') {
        return <Fragment key={i}>{renderChildren(subUnit.children)}</Fragment>;
      }

      return (
        <StructuresUnitItem
          key={i}
          _key={`${_key}-${i}`}
          item={subUnit}
          paddingPx={paddingPx + 25}
          isDefaultOpen={children?.length === 1 || subUnit.data?.length === 1}
          withRequirements={withRequirements}
          suIdsSelected={suIdsSelected}
          setSuIdsSelected={setSuIdsSelected}
          npaId={npaId}
          path={`${path}\\${subUnit.levelTitle}: ${subUnit.value}`}
          addMandatory={addMandatory}
          setIsSuOpen={setIsSuOpen}
          isSusOpen={isSusOpen}
          didCloseInnerModal={didCloseInnerModal}
          knoVersionId={knoVersionId}
          supervisionVersionId={supervisionVersionId}
          created={created}
          updateNpaList={updateNpaList}
          inputRequirementsNpa={inputRequirementsNpa}
          requirementsErknmDisabled={requirementsErknmDisabled}
          structuresUnitsFilters={structuresUnitsFilters}
          mrIds={mrIds}
          ignoredNpaIds={ignoredNpaIds}
          usedNpa={usedNpa}
        />
      );
    });
  };

  return (
    <div className={styles.Wrapper} id={item.id || undefined}>
      <div
        className={`${styles.UnitWrapper} ${!withRequirements && item.id && styles.UnitWrapperHover}`}
        style={{ marginLeft: `calc(14% + ${paddingPx}px)`, width: `calc(100% - 14% - ${paddingPx}px)` }}
      >
        {item.children?.length || (withRequirements && (Array.isArray(item.data) || item.dataSize)) ? (
          <div onClick={setIsOpenFunction}>
            <IconCollapse className={`${styles.IconCollapse} ${isOpen && styles.IconCollapseOpen}`} />
          </div>
        ) : (
          <div style={{ width: 15 }} />
        )}
        {!item.children?.length && !(withRequirements && (Array.isArray(item.data) || item.dataSize)) && (
          <Checkbox id={item.id || ''} theme="system" checked={isSelectedSUByPath} onChange={onClickSU} />
        )}
        <span
          style={{ cursor: !withRequirements && !item.children?.length ? 'pointer' : 'default' }}
          onClick={onClickSU}
        >
          {item.levelTitle}: {item.value}
        </span>
      </div>
      {withRequirements && isOpen && item.data && (
        <>
          <span
            className={styles.MandatoryHelpText}
            style={{ marginLeft: `calc(14% + ${paddingPx}px)`, marginBottom: '8px' }}
          >
            Формулировки из {mandatoryTitle}:
            {!requirementsErknmDisabled &&
              !inputRequirementsNpa &&
              (!item.sourceRot || (item.sourceRot && (item.dataSize === 0 || !item.data.some(i => i.sourceRot)))) && (
                <div className={styles.ButtonCreateMandatoryWrapper}>
                  <Button
                    text="Добавить формулировку"
                    variant="link"
                    className={styles.CreateUnitButton}
                    startIcon={styles.AddIcon}
                    onClick={() => addMandatory(path, parseInt(item.id || '-1', 10))}
                    type="button"
                  />
                </div>
              )}
          </span>
          {item.dataSize === 0 && !isLoading && (
            <div className={styles.EmptySearch} style={{ marginLeft: `calc(14% + ${paddingPx}px)` }}>
              <span className={styles.InfoIcon}></span>
              <span className={styles.InformationSupportText}>
                {item.source === 'ROT' && !requirementsErknmDisabled && item.children.length === 0
                  ? 'Для этой структурной единицы отсутствует формулировка обязательных требований в ФГИС РОТ, добавьте формулировку через внутренний справочник'
                  : `Для этой структурной единицы отсутствуют формулировки обязательных требований, добавьте новую
                формулировку ${item.source === 'ROT' ? 'в ФГИС РОТ' : ''}`}
              </span>
            </div>
          )}
          {item.data.length > 0 &&
            item.data.map((mandatory, i) => {
              return (
                <div
                  id={mandatory.id.toString()}
                  key={i}
                  className={styles.SelectThisSUBlock}
                  style={{ marginLeft: `calc(14% + ${paddingPx}px)`, marginBottom: '8px' }}
                >
                  <Checkbox
                    id={`checkbox-${mandatory.id.toString()}`}
                    theme="system"
                    checked={selectedMandatoryById(mandatory.id)}
                    onChange={() => onSelectedMandatory(mandatory.id)}
                  />
                  <span onClick={() => onSelectedMandatory(mandatory.id)}>{mandatory.title}</span>
                </div>
              );
            })}
        </>
      )}
      {isOpen && renderChildren(item.children)}

      {isOpen && isLoading && <Loader />}
    </div>
  );
};

export { StructuresUnitItem };
